import { Ref } from 'vue'

import {
  AssetClass,
  AssetPriceForm,
  TagClass,
  TransactionClass
} from '@types'
import { InputWizardPreset, WizardEntity } from './types'

import { ASSET_FIELD } from '@/const'
import { ASSET_CLASS, INVESTMENT_STRATEGY } from './const'
import { TagsBunchStore } from '@/store/tags/utils/types'

export const resetData = (
  asset: Ref<AssetClass | undefined>,
  account: Ref<AssetClass | undefined>,
  transaction: Ref<TransactionClass | undefined>,
  prices: Ref<AssetPriceForm[]>,
  tags: Ref<Record<string, string | undefined>>,
) => {
  account.value = undefined
  asset.value = undefined
  transaction.value = undefined
  prices.value = []
  tags.value[ASSET_CLASS] = undefined
  tags.value[INVESTMENT_STRATEGY] = undefined
}

const initTransaction = (type: string, date: string) => {
  return new TransactionClass({
    type,
    date,
    entries: [
      { account_id: '', amount: 1, asset_id: '' },
      {
        account_id: '',
        amount: null,
        asset_id: '',
      },
    ],
  }, false, false)
}

export const initData = (
  type: string,
  date: string,
  asset: Ref<AssetClass | undefined>,
  account: Ref<AssetClass | undefined> | undefined,
  transaction: Ref<TransactionClass | undefined> | undefined,
  prices: Ref<AssetPriceForm[]>,
  tags: Ref<Record<string, string | undefined>>,
  repositoryId: string,
  currency: string,
  assetClassList: string[],
  investmentStrategyList: string[],
  accountType?: string,
  assetType?: string,
  preset?: InputWizardPreset,
) => {
  if (account) {
    account.value =
    preset?.account ||
    new AssetClass({
      type: accountType,
      name: '',
      ticker: '',
    })
    account.value.field<string>(ASSET_FIELD.REPOSITORY_ID).value = repositoryId
    account.value.field<string>(ASSET_FIELD.CURRENCY).value = currency
  }
  asset.value =
    preset?.asset ||
    new AssetClass({
      type: assetType,
      name: '',
      ticker: '',
    })
  asset.value.field<string>(ASSET_FIELD.REPOSITORY_ID).value = repositoryId
  asset.value.field<string>(ASSET_FIELD.CURRENCY).value = currency
  if (transaction || preset?.transaction) {
    transaction!.value = preset?.transaction || initTransaction(type, date)
  }
  if (preset?.prices) {
    prices.value = preset.prices
  }
  if (preset?.tags) {
    tags.value = preset.tags
  } else {
    tags.value[ASSET_CLASS] = assetClassList[0]
    tags.value[INVESTMENT_STRATEGY] = investmentStrategyList[0]
  }
  setTimeout(() => {
    account?.value?.validateData()
    asset.value?.validateData()
    transaction?.value?.validateData()
  }, 0)
}

export const processTags = async (
  tags: Record<string, string | undefined>,
  tagsBunch: TagsBunchStore,
) => {
  const tagsIds = []
  for await (const [tag_name, tag_value] of Object.entries(tags)) {
    if (!tag_value) continue
    const tagInstance = tagsBunch.getElementByPair(tag_name, tag_value)
    let tagId
    if (tagInstance?.id) {
      tagId = tagInstance?.id
    } else {
      const tag = new TagClass({ tag_name, tag_value })
      await tag.store()
      tagId = tag.id
    }
    tagsIds.push(tagId)
  }
  return tagsIds
}

export const addPostfixToAssetTicker = (asset: AssetClass, account: AssetClass) => {
  const assetName = asset.field<string>(ASSET_FIELD.TICKER).value
  const postfix = ` (${account.field<string>(ASSET_FIELD.NAME).value})`
  if (assetName.endsWith(postfix)) {
    return
  }
  asset.field<string>(ASSET_FIELD.TICKER).value = `${assetName}${postfix}`
}

// Create a new entity with its own account and transaction.
export const createWizardEntity = (
  key: string,
  type: string,
  date: string,
  repositoryId: string,
  currency: string,
  useExisting: boolean,
  accountType?: string,
): WizardEntity => {
  const account = new AssetClass({
    type: accountType,
    name: '',
    ticker: '',
  })
  account.field<string>(ASSET_FIELD.REPOSITORY_ID).value = repositoryId
  account.field<string>(ASSET_FIELD.CURRENCY).value = currency

  const transaction = initTransaction(type, date)

  setTimeout(() => {
    account.validateData()
    transaction.validateData()
  }, 0)

  return {
    key,
    useExisting,
    account,
    transaction,
    prices: [],
    contributions: [],
    distributions: [],
  }
}

// Get display name for the investing entity.
export const getEntityAccountName = (
  entity: WizardEntity,
  getAccountById: (id: string) => AssetClass | undefined,
): string => {
  if (entity.useExisting) {
    const accountId = entity.transaction.entry1().value?.account_id
    const account = accountId ? getAccountById(accountId) : undefined
    return account?.field<string>(ASSET_FIELD.NAME).value || 'Investing entity'
  }
  return (
    entity.account.field<string>(ASSET_FIELD.NAME).value ||
    'New investing entity'
  )
}
